import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from "react";
import Widget from "./Widget.js";
import { Dropdown } from 'react-bootstrap';
import WidgetSettingModal from "./WidgetSettingModal.js";
import DropDownModal from "./DropDownModal.js";
import { Responsive, WidthProvider } from 'react-grid-layout';
import '../../node_modules/react-grid-layout/css/styles.css';
import '../../node_modules/react-resizable/css/styles.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrashAlt, faEllipsisH, faCog, faEdit, faCopy, faPaste } from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import _ from "lodash";
import swal from "sweetalert";
import { createPortal } from 'react-dom';
import { produce } from 'immer';
import Cookie from "universal-cookie";
import { decrypt } from "./useCryptoJS";
const ResponsiveGridLayout = WidthProvider(Responsive);

const defaultOptions = {
  colorStart: "#6fadcf",
  colorStop: void 0,
  gradientType: 0,
  strokeColor: "#e0e0e0",
  generateGradient: true,
  pointer: { length: 0.41, strokeWidth: 0.035, iconScale: 1.0, color: "white" },
  staticLabels: { font: "10px sans-serif", labels: [0, 10, 30, 50, 70, 100], fractionDigits: 0, color: '#ffffffff' },
  staticZones: [
    { strokeStyle: "#F03E3E", min: 0, max: 10 },
    { strokeStyle: "#FFDD00", min: 10, max: 30 },
    { strokeStyle: "#30B32D", min: 30, max: 50 },
    { strokeStyle: "#FFDD00", min: 50, max: 70 },
    { strokeStyle: "#F03E3E", min: 70, max: 100 },
  ],
  angle: 0,
  lineWidth: 0.25,
  radiusScale: 1.0,
  fontSize: 40,
  limitMax: false,
  limitMin: false,
  highDpiSupport: true,
};

const generateRandomId = () => {
  const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const idLength = 10;
  return Array.from({ length: idLength }, () => characters[Math.floor(Math.random() * characters.length)]).join('');
};

const DropdownPortal = ({ children, ...props }) => {
  return createPortal(
    <Dropdown.Menu {...props}>{children}</Dropdown.Menu>,
    document.body
  );
};

const MuuriWidget = forwardRef((props, ref) => {
  const widgetsNotRequiringSource = ["mqtt_writer", "image_widget", "nested_pane", "custom_form_widget", "push_button"];

  const [dataIndex, setDataIndex] = useState(0);
  const [currentPaneIndex, setCurrentPaneIndex] = useState(0);
  const [currentWidgetIndex, setCurrentWidgetIndex] = useState(0);
  const [currentNestedWidgetIndex, setCurrentNestedWidgetIndex] = useState(null);
  const [modal, setModal] = useState(false);
  const [isModalActive, setIsModalActive] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editWidgetModal, setEditWidgetModal] = useState(false);
  const [changeSourceModal, setChangeSourceModal] = useState(false);
  const [isChildDrag, setChildDrag] = useState(false);
  const [isParentDraggable, setIsParentDraggable] = useState(true);
  const [isNestedWidgetDraggable, setIsNestedWidgetDraggable] = useState(true)
  const parentGridRef = useRef(null);
  const nestedGridRef = useRef(null);
  const [currentBreakpoint, setCurrentBreakpoint] = useState("lg");
  const [compactType, setCompactType] = useState("horizontal");
  const [mounted, setMounted] = useState(false);
  const [layouts, setLayouts] = useState({});
  const [initialSetup, setInitialSetup] = useState(true);
  const [copiedWidget, setCopiedWidget] = useState(null);
  const [copiedPane, setCopiedPane] = useState(null);
  const [currentTarget, setCurrentTarget] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const cookies = new Cookie();
  let urdashboard = "0000";
  let userToken = null;
  try {
    urdashboard = decrypt(cookies.get("urdashboard")) || "0000";
    userToken = cookies.get("userToken");
  } catch (error) {
    console.error("Error parsing user data:", error);
  }

  const canView = urdashboard.charAt(0) === "1"; // View dashboards
  const canEdit = urdashboard.charAt(1) === "1"; // Edit dashboards (move/resize)
  const canCreate = urdashboard.charAt(2) === "1"; // Create new panes/widgets
  const canDelete = urdashboard.charAt(3) === "1"; // Delete dashboards/panes/widgets

  const stopPropagation = useCallback((e) => e.stopPropagation(), []);

  const moveWidget = useCallback((path, direction) => {
    const newPaneArray = produce(props.paneArray, draft => {
      let current = draft;
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]] || !current[path[i]].widgetsArray) {
          console.warn('Invalid path for moveWidget:', path);
          return;
        }
        current = current[path[i]].widgetsArray;
      }
      const widget = current[path[path.length - 1]];
      const delta = direction === 'left' ? -1 : 1;
      const newX = widget.dataGrid.wx + delta;
      const cols = 12;
      if (newX >= 0 && newX <= cols - widget.dataGrid.ww) {
        widget.dataGrid.wx = newX;
        Object.keys(widget.dataGrid.layouts).forEach(breakpoint => {
          widget.dataGrid.layouts[breakpoint].wx = newX;
        });
      }
    });
    if (!_.isEqual(newPaneArray, props.paneArray)) {
      props.setPaneArray(newPaneArray);
    }
  }, [props.paneArray, props.setPaneArray]);

  const fixNestedPanes = useCallback((paneArray) => {
    return produce(paneArray, draft => {
      draft.forEach(pane => {
        if (!Array.isArray(pane.widgetsArray)) {
          pane.widgetsArray = [];
        }
        pane.widgetsArray.forEach(widget => {
          if (widget.widget_type === 'nested_pane' && !Array.isArray(widget.widgetsArray)) {
            widget.widgetsArray = [];
          }
        });
      });
    });
  }, []);

  const getLayoutsFromPanes = useCallback((paneArray) => {
    const layouts = { lg: [], md: [], sm: [], xs: [] };
    const breakpointConfig = {
      lg: { cols: 12, rowHeight: 30, margin: [10, 10] },
      md: { cols: 10, rowHeight: 30, margin: [10, 10] },
      sm: { cols: 6, rowHeight: 30, margin: [10, 10] },
      xs: { cols: 4, rowHeight: 30, margin: [10, 10] },
    };

    paneArray.forEach((pane, ind) => {
      Object.keys(breakpointConfig).forEach((breakpoint) => {
        const config = breakpointConfig[breakpoint];
        const layoutData = pane.layouts?.[breakpoint] || { x: pane.x, y: pane.y, w: pane.w, h: pane.h };
        layouts[breakpoint].push({
          i: ind.toString(),
          x: layoutData.x % config.cols,
          y: layoutData.y,
          w: Math.min(layoutData.w, config.cols),
          h: layoutData.h,
          minW: 4,
          minH: 9,
          isDraggable: true,
          isResizable: true,
        });
      });
    });
    return layouts;
  }, []);

  const getNestedWidgetLayouts = useCallback((nestedWidgets, prefix) => {
    const safeNestedWidgets = Array.isArray(nestedWidgets) ? nestedWidgets : [];
    return {
      lg: safeNestedWidgets.map((widget, idx) => ({
        i: `${prefix}${idx}`,
        x: widget.dataGrid.layouts?.lg?.wx || widget.dataGrid.wx || 0,
        y: widget.dataGrid.layouts?.lg?.wy || widget.dataGrid.wy || 0,
        w: widget.dataGrid.layouts?.lg?.ww || widget.dataGrid.ww || 4,
        h: widget.dataGrid.layouts?.lg?.wh || widget.dataGrid.wh || 7,
        minW: 2,
        minH: 2,
        maxW: widget.dataGrid.wmaxWidth || 12,
        maxH: widget.dataGrid.wmaxHeight || 20,
      })),
      md: safeNestedWidgets.map((widget, idx) => ({
        i: `${prefix}${idx}`,
        x: widget.dataGrid.layouts?.md?.wx || widget.dataGrid.wx || 0,
        y: widget.dataGrid.layouts?.md?.wy || widget.dataGrid.wy || 0,
        w: widget.dataGrid.layouts?.md?.ww || widget.dataGrid.ww || 4,
        h: widget.dataGrid.layouts?.md?.wh || widget.dataGrid.wh || 7,
        minW: 2,
        minH: 2,
        maxW: widget.dataGrid.wmaxWidth || 10,
        maxH: widget.dataGrid.wmaxHeight || 20,
      })),
      sm: safeNestedWidgets.map((widget, idx) => ({
        i: `${prefix}${idx}`,
        x: widget.dataGrid.layouts?.sm?.wx || widget.dataGrid.wx || 0,
        y: widget.dataGrid.layouts?.sm?.wy || widget.dataGrid.wy || 0,
        w: widget.dataGrid.layouts?.sm?.ww || widget.dataGrid.ww || 3,
        h: widget.dataGrid.layouts?.sm?.wh || widget.dataGrid.wh || 6,
        minW: 2,
        minH: 2,
        maxW: widget.dataGrid.wmaxWidth || 6,
        maxH: widget.dataGrid.wmaxHeight || 20,
      })),
      xs: safeNestedWidgets.map((widget, idx) => ({
        i: `${prefix}${idx}`,
        x: widget.dataGrid.layouts?.xs?.wx || widget.dataGrid.wx || 0,
        y: widget.dataGrid.layouts?.xs?.wy || widget.dataGrid.wy || 0,
        w: widget.dataGrid.layouts?.xs?.ww || widget.dataGrid.ww || 2,
        h: widget.dataGrid.layouts?.xs?.wh || widget.dataGrid.wh || 5,
        minW: 2,
        minH: 2,
        maxW: widget.dataGrid.wmaxWidth || 4,
        maxH: widget.dataGrid.wmaxHeight || 20,
      })),
    };
  }, []);

  useImperativeHandle(ref, () => ({
    updateLayouts: (newPaneArray) => {
      const fixedPaneArray = fixNestedPanes(newPaneArray);
      const newLayouts = getLayoutsFromPanes(fixedPaneArray);
      setLayouts(newLayouts);
    },
  }));

  const prevDashboard = useRef(null);
  const prevPaneArray = useRef(null);

  const initializeLayout = useCallback(() => {
    if (props.paneArray && props.paneArray.length > 0 && !_.isEqual(prevDashboard.current, props.newDashboard)) {
      const fixedPaneArray = fixNestedPanes(props.paneArray);
      const newLayouts = getLayoutsFromPanes(fixedPaneArray);
      setLayouts(newLayouts);
      setMounted(true);
      setInitialSetup(false);
      prevDashboard.current = props.newDashboard;
      if (!_.isEqual(fixedPaneArray, props.paneArray)) {
        props.setPaneArray(fixedPaneArray);
      }
    }
  }, [props.paneArray, props.setPaneArray]);

  useEffect(() => {
    if (!_.isEqual(prevPaneArray.current, props.paneArray)) {
      initializeLayout();
      prevPaneArray.current = props.paneArray;
    }
  }, [props.paneArray, initializeLayout]);

  const onLayoutChange = useCallback(
    _.debounce((layout, allLayouts) => {
      if (initialSetup) return;
      if (!Array.isArray(props.paneArray)) return;
      const newPaneArray = produce(props.paneArray, draft => {
        draft.forEach((pane, index) => {
          const updatedPane = allLayouts[currentBreakpoint].find((item) => item.i === index.toString());
          if (updatedPane) {
            pane.x = updatedPane.x;
            pane.y = updatedPane.y;
            pane.w = updatedPane.w;
            pane.h = updatedPane.h;
            pane.layouts = { ...pane.layouts, [currentBreakpoint]: { x: updatedPane.x, y: updatedPane.y, w: updatedPane.w, h: updatedPane.h } };
          }
        });
      });
      if (!_.isEqual(newPaneArray, props.paneArray)) props.setPaneArray(newPaneArray);
      setLayouts(allLayouts);
    }, 50),
    [currentBreakpoint, props.paneArray, props.setPaneArray, initialSetup]
  );

  const onWidgetLayoutChange = useCallback((layout, paneIndex) => {
    const newPaneArray = produce(props.paneArray, draft => {
      const pane = draft[paneIndex];
      if (!pane || !Array.isArray(pane.widgetsArray)) {
        console.warn('Invalid pane at index:', paneIndex);
        return;
      }
      pane.widgetsArray.forEach((widget, widgetIndex) => {
        const widgetLayout = layout.find((item) => item.i === `${paneIndex}${widgetIndex}`);
        if (widgetLayout) {
          widget.dataGrid.wx = widgetLayout.x;
          widget.dataGrid.wy = widgetLayout.y;
          widget.dataGrid.ww = widgetLayout.w;
          widget.dataGrid.wh = widgetLayout.h;
          widget.dataGrid.layouts[currentBreakpoint] = { wx: widgetLayout.x, wy: widgetLayout.y, ww: widgetLayout.w, wh: widgetLayout.h };
        }
      });
    });
    if (!_.isEqual(newPaneArray, props.paneArray)) props.setPaneArray(newPaneArray);
  }, [props.paneArray, props.setPaneArray, currentBreakpoint]);

  const onNestedWidgetLayoutChange = useCallback((layout, path) => {
    const newPaneArray = produce(props.paneArray, draft => {
      let current = draft;
      for (let i = 0; i < path.length; i++) {
        if (i === 0) {
          current = current[path[i]];
        } else {
          if (!current || !current.widgetsArray) {
            console.warn('Path does not exist in current paneArray:', path.slice(0, i + 1), current);
            return;
          }
          current = current.widgetsArray[path[i]];
        }
      }
      if (!current || current.widget_type !== 'nested_pane') {
        console.warn('Target is not a valid nested pane:', path, current);
        return;
      }
      if (!Array.isArray(current.widgetsArray)) {
        current.widgetsArray = [];
      }
      current.widgetsArray.forEach((widget, widgetIndex) => {
        const widgetLayout = layout.find((item) => item.i === `${path.join('')}${widgetIndex}`);
        if (widgetLayout) {
          widget.dataGrid.wx = widgetLayout.x;
          widget.dataGrid.wy = widgetLayout.y;
          widget.dataGrid.ww = widgetLayout.w;
          widget.dataGrid.wh = widgetLayout.h;
          widget.dataGrid.layouts[currentBreakpoint] = { wx: widgetLayout.x, wy: widgetLayout.y, ww: widgetLayout.w, wh: widgetLayout.h };
        }
      });
    });
    if (!_.isEqual(newPaneArray, props.paneArray)) props.setPaneArray(newPaneArray);
  }, [props.paneArray, props.setPaneArray, currentBreakpoint]);

  const toggleModal = () => setModal(false);
  const editWidgeToggleModal = () => {
    setEditWidgetModal(false);
    setEditMode(false);
  };
  const toggleEditModal = () => setEditModal(false);

  useEffect(() => {
    document.body.classList.toggle("active-modal", isModalActive);
  }, [isModalActive]);

  const deletePane = useCallback((ind) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this pane?");
    if (!confirmDelete) return; // stop if user cancels
    const newPaneArray = produce(props.paneArray, draft => {
      draft.splice(ind, 1);
    });
    props.setPaneArray(newPaneArray);
    const newLayouts = getLayoutsFromPanes(newPaneArray);
    setLayouts(newLayouts);
  }, [props.paneArray, props.setPaneArray]);

  const deleteWidget = useCallback((path) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this widget?");
    if (!confirmDelete) return; // stop if user cancels
    const newPaneArray = produce(props.paneArray, draft => {
      let current = draft;
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]] || !current[path[i]].widgetsArray) {
          console.warn('Invalid path for deleteWidget:', path);
          return;
        }
        current = current[path[i]].widgetsArray;
      }
      current.splice(path[path.length - 1], 1);
    });
    if (!_.isEqual(newPaneArray, props.paneArray)) props.setPaneArray(newPaneArray);
  }, [props.paneArray, props.setPaneArray]);

  const addWidget = useCallback((id) => {
    setCurrentTarget({ type: 'pane', path: [id] });
    setCurrentPaneIndex(id);
    setCurrentWidgetIndex(0);
    setCurrentNestedWidgetIndex(null);
    setModal(true);
    setEditMode(false);
  }, []);

  const addWidgetToNestedPane = useCallback((path) => {
    let target = props.paneArray;
    for (let i = 0; i < path.length; i++) {
      if (i === 0) {
        target = target[path[i]];
      } else {
        if (!target || !target.widgetsArray) {
          console.warn('Invalid path for addWidgetToNestedPane:', path);
          return;
        }
        target = target.widgetsArray[path[i]];
      }
    }
    if (!target || target.widget_type !== 'nested_pane') {
      console.warn('Target is not a valid nested pane:', path, target);
      return;
    }
    if (!Array.isArray(target.widgetsArray)) {
      const newPaneArray = produce(props.paneArray, draft => {
        let current = draft;
        for (let i = 0; i < path.length; i++) {
          if (i === 0) {
            current = current[path[i]];
          } else {
            current = current.widgetsArray[path[i]];
          }
        }
        current.widgetsArray = [];
      });
      props.setPaneArray(newPaneArray);
    }
    setCurrentTarget({ type: 'nested_pane', path });
    setCurrentPaneIndex(path[0]);
    setCurrentWidgetIndex(path[1]);
    setCurrentNestedWidgetIndex(path.length > 2 ? path[2] : null);
    setModal(true);
    setEditMode(false);
  }, [props.paneArray, props.setPaneArray]);

  const updatePaneName = useCallback((index, newName) => {
    const newPaneArray = produce(props.paneArray, draft => {
      draft[index].name = newName;
    });
    props.setPaneArray(newPaneArray);
  }, [props.paneArray, props.setPaneArray]);

  const updateNestedPaneName = useCallback((path, newName) => {
    const newPaneArray = produce(props.paneArray, draft => {
      let current = draft;
      for (let i = 0; i < path.length - 1; i++) {
        if (!current[path[i]] || !current[path[i]].widgetsArray) {
          console.warn('Invalid path for updateNestedPaneName:', path);
          return;
        }
        current = current[path[i]].widgetsArray;
      }
      if (current[path[path.length - 1]]) {
        current[path[path.length - 1]].widget_name = newName;
      }
    });
    if (!_.isEqual(newPaneArray, props.paneArray)) props.setPaneArray(newPaneArray);
  }, [props.paneArray, props.setPaneArray]);

  const deepCloneWidget = useCallback((widget) => {
    const newId = generateRandomId();
    if (widget.widget_type === 'nested_pane') {
      return {
        ...widget,
        id: newId,
        widgetsArray: Array.isArray(widget.widgetsArray) ? widget.widgetsArray.map(deepCloneWidget) : [],
      };
    }
    return { ...widget, id: newId };
  }, []);

  const widgetSettupHandler = useCallback((widgetData = null) => {
    let newPaneArray;
    if (!editMode && widgetData) {
      const newWidget = {
        id: generateRandomId(),
        ...widgetData,
        dashboard_name: props.newDashboard,
        dataGrid: {
          wx: 0,
          wy: 0,
          ww: 4,
          wh: 7,
          wminWidth: 3,
          wmaxWidth: 12,
          wminHeight: 5,
          layouts: {
            lg: { wx: 0, wy: 0, ww: 4, wh: 7 },
            md: { wx: 0, wy: 0, ww: 4, wh: 7 },
            sm: { wx: 0, wy: 0, ww: 3, wh: 6 },
            xs: { wx: 0, wy: 0, ww: 2, wh: 5 },
          },
        },
        widgetNameFontSize: widgetData.widgetNameFontSize || 16,
        ...(widgetData.widget_type === 'nested_pane' && { widgetsArray: Array.isArray(widgetData.widgetsArray) ? widgetData.widgetsArray.map(deepCloneWidget) : [] }),
      };
      newPaneArray = produce(props.paneArray, draft => {
        let target = draft;
        const path = currentTarget.path;
        for (let i of path) {
          target = target[i].widgetsArray;
        }
        target.push(newWidget);
      });
    } else if (editMode && widgetData) {
      const currentWidget = getCurrentWidget();
      const updatedWidget = {
        id: currentWidget.id,
        ...widgetData,
        dashboard_name: props.newDashboard,
        dataGrid: currentWidget.dataGrid,
        widgetNameFontSize: widgetData.widgetNameFontSize || currentWidget.widgetNameFontSize || 16,
        ...(widgetData.widget_type === 'nested_pane' && { widgetsArray: Array.isArray(widgetData.widgetsArray) ? widgetData.widgetsArray.map(deepCloneWidget) : [] }),
      };
      newPaneArray = produce(props.paneArray, draft => {
        let current = draft;
        const path = currentTarget.path;
        for (let i = 0; i < path.length - 1; i++) {
          current = current[path[i]].widgetsArray;
        }
        current[path[path.length - 1]] = updatedWidget;
      });
    } else {
      let form = document.querySelector("#create-widget");
      if (!form) return;
      let formData = Object.fromEntries(new FormData(form).entries());

      const createWidget = () => {
        const id = generateRandomId();
        if (formData.widget_type === "gauge") {
          return {
            id,
            widget_type: formData.widget_type,
            widget_name: formData.widget_name,
            widget_unit: formData.widget_unit,
            dashboard_name: props.newDashboard,
            value: 0,
            datasource: [],
            datasets: [{ name: formData.widget_name }],
            dataGrid: {
              wx: 0,
              wy: 0,
              ww: 3,
              wh: 7,
              wminWidth: 2,
              wmaxWidth: 12,
              wminHeight: 5,
              layouts: {
                lg: { wx: 0, wy: 0, ww: 3, wh: 7 },
                md: { wx: 0, wy: 0, ww: 3, wh: 7 },
                sm: { wx: 0, wy: 0, ww: 3, wh: 7 },
                xs: { wx: 0, wy: 0, ww: 2, wh: 6 },
              },
            },
            widgetNameFontSize: parseInt(formData.widgetNameFontSize) || 16,
          };
        } else if (formData.widget_type === "nested_pane") {
          return {
            id,
            widget_type: "nested_pane",
            widget_name: formData.pane_name || "Nested Pane",
            dashboard_name: props.newDashboard,
            widgetsArray: [],
            datasets: [],
            dataGrid: {
              wx: 0,
              wy: 0,
              ww: 4,
              wh: 7,
              wminWidth: 4,
              wmaxWidth: 12,
              wminHeight: 5,
              layouts: {
                lg: { wx: 0, wy: 0, ww: 4, wh: 7 },
                md: { wx: 0, wy: 0, ww: 4, wh: 7 },
                sm: { wx: 0, wy: 0, ww: 3, wh: 6 },
                xs: { wx: 0, wy: 0, ww: 2, wh: 5 },
              },
            },
            widgetNameFontSize: parseInt(formData.widgetNameFontSize) || 16,
          };
        }
        return null;
      };

      const newWidget = createWidget();
      if (newWidget) {
        newPaneArray = produce(props.paneArray, draft => {
          let target = draft;
          const path = currentTarget.path;
          for (let i of path) {
            target = target[i].widgetsArray;
          }
          target.push(newWidget);
        });
      }
    }

    if (newPaneArray && !_.isEqual(newPaneArray, props.paneArray)) {
      props.setPaneArray(newPaneArray);
    }
    setModal(false);
    setEditWidgetModal(false);
    setCurrentTarget(null);
    setCurrentPaneIndex(0);
    setCurrentWidgetIndex(0);
    setCurrentNestedWidgetIndex(null);
  }, [props.paneArray, props.setPaneArray, currentTarget, editMode]);

  const copyPane = useCallback((paneIndex) => {
    const paneToCopy = props.paneArray[paneIndex];
    setCopiedPane({
      ...paneToCopy,
      widgetsArray: paneToCopy.widgetsArray.map(deepCloneWidget),
    });
  }, [props.paneArray, deepCloneWidget]);

  const pastePane = useCallback((targetIndex) => {
    if (!copiedPane) {
      swal("Nothing to paste!", "Please copy a pane first", "warning");
      return;
    }
    const targetPane = props.paneArray[targetIndex];
    const newPane = {
      ...copiedPane,
      x: targetPane.x + targetPane.w,
      y: targetPane.y,
      widgetsArray: copiedPane.widgetsArray.map(deepCloneWidget),
    };
    const newPaneArray = produce(props.paneArray, draft => {
      draft.splice(targetIndex + 1, 0, newPane);
    });
    props.setPaneArray(newPaneArray);
    const newLayouts = getLayoutsFromPanes(newPaneArray);
    setLayouts(newLayouts);
  }, [props.paneArray, props.setPaneArray, copiedPane, deepCloneWidget]);

  const copyWidget = useCallback((path) => {
    if (path.length < 2) {
      console.warn('Invalid path for copyWidget: path too short', path);
      return;
    }
    let currentArray = props.paneArray[path[0]]?.widgetsArray;
    if (!Array.isArray(currentArray)) {
      console.warn('Invalid pane or no widgetsArray:', path[0]);
      return;
    }
    let widgetToCopy;
    for (let index = 1; index < path.length; index++) {
      const i = path[index];
      if (!currentArray[i]) {
        console.warn('Invalid widget index in path:', path, i);
        return;
      }
      widgetToCopy = currentArray[i];
      if (index < path.length - 1) {
        if (!Array.isArray(widgetToCopy.widgetsArray)) {
          console.warn('Expected nested pane but no widgetsArray:', path, i);
          return;
        }
        currentArray = widgetToCopy.widgetsArray;
      }
    }
    setCopiedWidget(deepCloneWidget(widgetToCopy));


  }, [props.paneArray, deepCloneWidget]);



  const pasteWidget = useCallback((path) => {
    if (!copiedWidget) return;
    const newPaneArray = produce(props.paneArray, draft => {
      let target = draft;
      for (let i of path) {
        target = target[i].widgetsArray;
      }
      target.push(deepCloneWidget(copiedWidget));
    });
    if (!_.isEqual(newPaneArray, props.paneArray)) props.setPaneArray(newPaneArray);
  }, [props.paneArray, props.setPaneArray, copiedWidget, deepCloneWidget]);

  const changeSource = useCallback((path) => {
    let widget = props.paneArray;
    for (let i of path) {
      if (!widget[i]) {
        console.warn('Invalid path for changeSource:', path);
        return;
      }
      widget = widget[i].widgetsArray ? widget[i].widgetsArray : widget[i];
    }
    if (widgetsNotRequiringSource.includes(widget.widget_type)) return;

    setCurrentPaneIndex(path[0]);
    setCurrentWidgetIndex(path[1]);
    setCurrentNestedWidgetIndex(path.length > 2 ? path[2] : null);
    setCurrentTarget({ type: 'widget', path });
    setChangeSourceModal(true);
  }, [props.paneArray]);

  const editWidget = useCallback((path) => {
    let widget = props.paneArray;
    for (let i of path) {
      if (!widget[i]) {
        console.warn('Invalid path for editWidget:', path);
        return;
      }
      widget = widget[i].widgetsArray ? widget[i].widgetsArray : widget[i];
    }
    setCurrentPaneIndex(path[0]);
    setCurrentWidgetIndex(path[1]);
    setCurrentNestedWidgetIndex(path.length > 2 ? path[2] : null);
    setCurrentTarget({ type: 'widget', path });
    setEditWidgetModal(true);
    setEditMode(true);
  }, [props.paneArray]);

  const handleMouseDown = useCallback(_.throttle((event) => {
    setChildDrag(true);
    setIsParentDraggable(false);
    setIsNestedWidgetDraggable(false);
  }, 100), []);


  const handleNestedWidgetMouseDown = useCallback(_.throttle((event) => {
    setIsNestedWidgetDraggable(false);
  }, 100), []);

  const handleMouseUp = () => {
    setChildDrag(false);
    setIsParentDraggable(true);
    setIsNestedWidgetDraggable(true);
  };

  const handleParentDragDown = () => {
    setIsParentDraggable(true);
  };

  const handleNestedWidgetDragDown = () => {
    setIsNestedWidgetDraggable(true);
  };

  const onBreakpointChange = useCallback((breakpoint) => {
    setCurrentBreakpoint(breakpoint);
    const newLayouts = getLayoutsFromPanes(props.paneArray);
    setLayouts(newLayouts);
  }, [props.paneArray]);

  const onCompactTypeChange = () => {
    setCompactType((prev) => (prev === "horizontal" ? "vertical" : prev === "vertical" ? null : "horizontal"));
  };

  const MemoizedWidget = React.memo(Widget, (prevProps, nextProps) => {
    return (
      prevProps.widget === nextProps.widget &&
      prevProps.setPaneArray === nextProps.setPaneArray &&
      _.isEqual(prevProps.widgetPath, nextProps.widgetPath)
    );
  });

  const MemoizedDropdown = React.memo(({ widget, path, changeSource, editWidget, deleteWidget, copyWidget, pasteWidget, copiedWidget }) => (
    <Dropdown>
      <Dropdown.Toggle
        id="dropdown-basic-button"
        variant="secondary"
        size="sm"
        onMouseDown={stopPropagation}
        onTouchStart={stopPropagation}
      >
        <FontAwesomeIcon icon={faEllipsisH} />
      </Dropdown.Toggle>
      <DropdownPortal>
        {!widgetsNotRequiringSource.includes(widget.widget_type) && (
          <Dropdown.Item as="button" onClick={() => changeSource(path)} onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
            <FontAwesomeIcon icon={faCog} style={{ color: 'grey' }} /> Choose Source
          </Dropdown.Item>
        )}
        <Dropdown.Item as="button" onClick={() => editWidget(path)} onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
          <FontAwesomeIcon icon={faEdit} style={{ color: 'blue' }} /> Edit
        </Dropdown.Item>
        <Dropdown.Item as="button" onClick={() => deleteWidget(path)} onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
          <FontAwesomeIcon icon={faTrashAlt} style={{ color: 'red' }} /> Delete
        </Dropdown.Item>
        <Dropdown.Item as="button" onClick={() => copyWidget(path)} onMouseDown={stopPropagation} onTouchStart={stopPropagation}>
          <FontAwesomeIcon icon={faCopy} style={{ color: 'green' }} /> Copy
        </Dropdown.Item>
        <Dropdown.Item as="button" onClick={() => pasteWidget(path.slice(0, -1))} onMouseDown={stopPropagation} onTouchStart={stopPropagation} disabled={!copiedWidget}>
          <FontAwesomeIcon icon={faPaste} style={{ color: 'blue' }} /> Paste
        </Dropdown.Item>
      </DropdownPortal>
    </Dropdown>
  ));

  const getCurrentWidget = useCallback(() => {
    let widget = props.paneArray;
    const path = currentTarget?.path || [];
    for (let i of path) {
      if (widget && widget[i]) {
        widget = widget[i].widgetsArray ? widget[i].widgetsArray : widget[i];
      } else {
        console.warn('Invalid path for getCurrentWidget:', path);
        return null;
      }
    }
    return widget;
  }, [props.paneArray, currentTarget]);

  // const renderNestedPaneButtons = (path) => {
  //   return (

  //     <div className="flex flex-row  items-center gap-1">
  //       {/* Add Widget (only if canCreate) */}
  //       {canCreate && (
  //         <button
  //           className="btn btn-circle"
  //           onClick={() => addWidgetToNestedPane(path)}
  //           onMouseDown={stopPropagation}
  //           onTouchStart={stopPropagation}
  //         >
  //           <FontAwesomeIcon icon={faPlus} size="xs" />
  //         </button>
  //       )}

  //       {/* Delete Pane (only if canDelete) */}
  //       {canDelete && (
  //         <button
  //           className="btn btn-circle"
  //           onClick={() => deleteWidget(path)}
  //           onMouseDown={stopPropagation}
  //           onTouchStart={stopPropagation}
  //         >
  //           <FontAwesomeIcon icon={faTrashAlt} size="xs" />
  //         </button>
  //       )}

  //       {/* Copy & Paste (only if canCreate) */}
  //       {canCreate && (
  //         <>
  //           <button
  //             className="btn btn-circle"
  //             onClick={() => copyWidget(path)}
  //             onMouseDown={stopPropagation}
  //             onTouchStart={stopPropagation}
  //           >
  //             <FontAwesomeIcon icon={faCopy} size="xs" />
  //           </button>
  //           <button
  //             className="btn btn-circle btn-xs bg-gray-600 hover:bg-gray-500 text-white transition-colors duration-150 disabled:opacity-40"
  //             onClick={() => pasteWidget(path.slice(0, -1))}
  //             onMouseDown={stopPropagation}
  //             onTouchStart={stopPropagation}
  //             disabled={!copiedWidget}
  //           >
  //             <FontAwesomeIcon icon={faPaste} size="xs" />
  //           </button>
  //         </>
  //       )}

  //       {path.length > 2 && (
  //         <>
  //           <button
  //             className="btn btn-sm btn-secondary"
  //             onClick={() => moveWidget(path, 'left')}
  //             onMouseDown={stopPropagation}
  //             onTouchStart={stopPropagation}
  //           >
  //             &lt;
  //           </button>
  //           <button
  //             className="btn btn-sm btn-secondary"
  //             onClick={() => moveWidget(path, 'right')}
  //             onMouseDown={stopPropagation}
  //             onTouchStart={stopPropagation}
  //           >
  //             &gt;
  //           </button>
  //         </>
  //       )}
  //     </div>
  //   );
  // };


  const renderNestedPaneButtons = (path) => {
    return (
      <div
        className="flex flex-row items-center gap-1 flex-nowrap overflow-x-auto"
        style={{
          whiteSpace: "nowrap",
          minWidth: "fit-content",
        }}
      >
        {/* Add Widget */}
        {canCreate && (
          <button
            className="btn btn-circle btn-xs shrink-0"
            onClick={() => addWidgetToNestedPane(path)}
            onMouseDown={stopPropagation}
            onTouchStart={stopPropagation}
          >
            <FontAwesomeIcon icon={faPlus} size="xs" />
          </button>
        )}

        {/* Delete Pane */}
        {canDelete && (
          <button
            className="btn btn-circle btn-xs shrink-0"
            onClick={() => deleteWidget(path)}
            onMouseDown={stopPropagation}
            onTouchStart={stopPropagation}
          >
            <FontAwesomeIcon icon={faTrashAlt} size="xs" />
          </button>
        )}

        {/* Copy & Paste */}
        {canCreate && (
          <>
            <button
              className="btn btn-circle btn-xs shrink-0"
              onClick={() => copyWidget(path)}
              onMouseDown={stopPropagation}
              onTouchStart={stopPropagation}
            >
              <FontAwesomeIcon icon={faCopy} size="xs" />
            </button>
            <button
              className="btn btn-circle btn-xs bg-gray-600 hover:bg-gray-500 text-white shrink-0"
              onClick={() => pasteWidget(path.slice(0, -1))}
              onMouseDown={stopPropagation}
              onTouchStart={stopPropagation}
              disabled={!copiedWidget}
            >
              <FontAwesomeIcon icon={faPaste} size="xs" />
            </button>
          </>
        )}

        {/* Move Buttons */}
        {path.length > 2 && (
          <>
            <button
              className="btn btn-xs btn-secondary shrink-0"
              onClick={() => moveWidget(path, 'left')}
              onMouseDown={stopPropagation}
              onTouchStart={stopPropagation}
            >
              &lt;
            </button>
            <button
              className="btn btn-xs btn-secondary shrink-0"
              onClick={() => moveWidget(path, 'right')}
              onMouseDown={stopPropagation}
              onTouchStart={stopPropagation}
            >
              &gt;
            </button>
          </>
        )}
      </div>
    );
  };

  const renderWidget = useCallback((widget, paneIndex, widgetIndex, nestedIndices = []) => {
    const path = [paneIndex, ...nestedIndices, widgetIndex];
    const fullIndex = path.join('');

    if (widget.widget_type === 'nested_pane') {
      return (
        <div
          key={fullIndex}
          style={{
            borderRadius: '0px',
            backgroundColor: '#2f2f34ff',
            margin: '5px',
            padding: '5px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: "0 3px 8px rgba(0, 0, 0, 0.4)",
            zIndex: 10,
          }}
          onMouseDown={() => setIsNestedWidgetDraggable(false)}
          onMouseUp={() => setIsNestedWidgetDraggable(true)}
          onMouseLeave={() => setIsNestedWidgetDraggable(true)}
         
          data-grid={{
            x: widget.dataGrid.layouts?.[currentBreakpoint]?.wx || widget.dataGrid.wx || 0,
            y: widget.dataGrid.layouts?.[currentBreakpoint]?.wy || widget.dataGrid.wy || 0,
            w: widget.dataGrid.layouts?.[currentBreakpoint]?.ww || widget.dataGrid.ww || 4,
            h: widget.dataGrid.layouts?.[currentBreakpoint]?.wh || widget.dataGrid.wh || 7,
            minW: 3,
            maxW: widget.dataGrid.wmaxWidth || 12,
            minH: 4,
            isDraggable: isNestedWidgetDraggable && canEdit,
            isResizable: canEdit,
          }}
        // onMouseDownCapture={handleNestedWidgetMouseDown}
        // onMouseUp={handleMouseUp}
        >

          <div className="flex items-center px-1 py-1 border-b border-gray-700 bg-gray-900"
            style={{ cursor: canEdit ? "move" : "default" }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <input
                value={widget.widget_name}
                onChange={e => {
                  updateNestedPaneName(path, e.target.value);
                }}
                disabled={!canEdit}
                className="flex-1 bg-transparent text-white text-xs font-medium leading-tight focus:outline-none focus:ring-0 disabled:opacity-50"
                style={{
                  border: "none",
                  padding: "2px 4px",
                }}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
              />
              {renderNestedPaneButtons(path)}
            </div>
          </div>


          <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: '1 1 auto', scrollbarWidth: 'thin' }}>
            <ResponsiveGridLayout
              className="layout nested-grid"
              layouts={getNestedWidgetLayouts(widget.widgetsArray || [], fullIndex)}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
              rowHeight={30}
              onLayoutChange={(layout) => onNestedWidgetLayoutChange(layout, path)}
              style={{ zIndex: 10 }}
              ref={nestedGridRef}
              onMouseDown={stopPropagation}
              onTouchStart={stopPropagation}
              isDraggable={canEdit}
              isResizable={canEdit}
            >
              {widget.widgetsArray?.map((nestedWidget, nestedIdx) =>
                renderWidget(nestedWidget, paneIndex, nestedIdx, [...nestedIndices, widgetIndex])
              )}
            </ResponsiveGridLayout>
          </div>
        </div>
      );
    } else {
      return (
        <div
          key={fullIndex}
          style={{
            height: '100%',
            borderRadius: '0px',
            // backgroundColor: '#28282b',
            backgroundColor: '#2c2c30ff',
            overflow: 'visible',
            zIndex: 10,
            position: 'relative',
            cursor: canEdit ? "move" : "default"
          }}

          className="nested-grid-item"
          onMouseDownCapture={handleMouseDown}
          onMouseUp={handleMouseUp}
          data-grid={{
            x: widget.dataGrid.layouts?.[currentBreakpoint]?.wx || widget.dataGrid.wx || 0,
            y: widget.dataGrid.layouts?.[currentBreakpoint]?.wy || widget.dataGrid.wy || 0,
            w: widget.dataGrid.layouts?.[currentBreakpoint]?.ww || widget.dataGrid.ww || 4,
            h: widget.dataGrid.layouts?.[currentBreakpoint]?.wh || widget.dataGrid.wh || 7,
            maxW: widget.dataGrid.wmaxWidth || 12,
          }}
        >
          {path.length > 2 && (
            <div
              className="widget-move"
              style={{
                position: 'absolute',
                top: '5px',
                left: '5px',
                transition: 'opacity 0.3s ease',
                zIndex: 11,
                display: 'flex',
                gap: '3px',
              }}
            >
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => moveWidget(path, 'left')}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
              >
                &lt;
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => moveWidget(path, 'right')}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
              >
                &gt;
              </button>
            </div>
          )}
          <div className="widget-settings">
            {(canCreate || canEdit) && (
              <MemoizedDropdown
                widget={widget}
                path={path}
                changeSource={changeSource}
                editWidget={editWidget}
                deleteWidget={deleteWidget}
                copyWidget={copyWidget}
                pasteWidget={pasteWidget}
                copiedWidget={copiedWidget}
              />
            )}
          </div>
          <div className="widget-container" style={{ height: '100%', overflow: 'hidden' }}>
            <div className="widget-content">
              <MemoizedWidget
                newDashboard={props.newDashboard}
                value={parseFloat(widget.value) || 0}
                minValue={0}
                maxValue={100}
                animationSpeed={32}
                options={defaultOptions}
                className="gauge-canvas"
                widget={widget}
                paneArray={props.paneArray}
                setPaneArray={props.setPaneArray}
                widgetPath={path}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
              />
            </div>
          </div>
        </div>
      );
    }
  }, [currentBreakpoint, handleMouseDown, handleMouseUp, stopPropagation, updateNestedPaneName, addWidgetToNestedPane, deleteWidget, pasteWidget, copiedWidget, moveWidget, changeSource, editWidget, copyWidget, deepCloneWidget, props.newDashboard, props.paneArray, props.setPaneArray]);

  if (!Array.isArray(props.paneArray)) {
    console.error("MuuriWidget: props.paneArray is required to be an array but received:", props.paneArray);
    return <div>Error: Invalid pane data</div>;
  }

  return (
    <>

      <div
        className="pane-display bg-gray-950 text-gray-100"
        style={{ overflow: "visible", height: "100%", zIndex: 10 }}
      >
        <div className="compaction-container">
          <button className="compaction-button" onClick={onCompactTypeChange}>
            Change Compaction Type
          </button>
          <span className="compaction-type">{_.capitalize(compactType) || "No Compaction"}</span>
        </div>

        <ResponsiveGridLayout
          {...props}
          layouts={layouts}
          onBreakpointChange={onBreakpointChange}
          onLayoutChange={onLayoutChange}
          measureBeforeMount={false}
          useCSSTransforms={true}
          compactType={compactType}
          preventCollision={!compactType}
          isDraggable={isParentDraggable && canEdit}       // 🔒 Only editable if allowed
          isResizable={canEdit}
          rowHeight={30}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
          transformScale={1}
          autoSize={true}
          verticalCompact={true}
          style={{ zIndex: 10 }}
          draggableCancel=".no-drag-zone"
        >
          {props.paneArray.map((pane, ind) => (
            <div
              key={ind.toString()}
              style={{
                backgroundColor: "#28282b",
                borderRadius: "0px",
                color: "white",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                border: "1px solid #2d2d2d",
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              }}
              ref={parentGridRef}
              className="parent-grid"
              onDoubleClick={handleParentDragDown}
              data-grid={{
                x: pane.layouts?.[currentBreakpoint]?.x || pane.x,
                y: pane.layouts?.[currentBreakpoint]?.y || pane.y,
                w: pane.layouts?.[currentBreakpoint]?.w || pane.w,
                h: pane.layouts?.[currentBreakpoint]?.h || pane.h,
                minW: 3,
                minH: 4,
              }}
            >
              <div
                className="flex items-center px-1 py-1 border-b border-gray-700 bg-gray-900"
                style={{ cursor: canEdit ? "move" : "default" }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  {/* Pane Name (editable only if canEdit) */}
                  <input
                    value={pane.name || `Pane ${ind}`}
                    onChange={(e) => canEdit && updatePaneName(ind, e.target.value)}
                    disabled={!canEdit}
                    className="flex-1 bg-transparent text-white text-xs font-medium leading-tight focus:outline-none focus:ring-0 disabled:opacity-50"
                    style={{
                      border: "none",
                      padding: "2px 4px",
                    }}
                    onMouseDown={stopPropagation}
                    onTouchStart={stopPropagation}
                  />

                  <div className="flex flex-row items-center gap-1">
                    {/* Add Widget (only if canCreate) */}
                    {canCreate && (
                      <button
                        className="btn btn-circle"
                        onClick={() => addWidget(ind)}
                        onMouseDown={stopPropagation}
                        onTouchStart={stopPropagation}
                      >
                        <FontAwesomeIcon icon={faPlus} size="xs" />
                      </button>
                    )}

                    {/* Delete Pane (only if canDelete) */}
                    {canDelete && (
                      <button
                        className="btn btn-circle"
                        onClick={() => deletePane(ind)}
                        onMouseDown={stopPropagation}
                        onTouchStart={stopPropagation}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} size="xs" />
                      </button>
                    )}

                    {/* Copy & Paste (only if canCreate) */}
                    {canCreate && (
                      <>
                        <button
                          className="btn btn-circle"
                          onClick={() => copyPane(ind)}
                          onMouseDown={stopPropagation}
                          onTouchStart={stopPropagation}
                        >
                          <FontAwesomeIcon icon={faCopy} size="xs" />
                        </button>
                        <button
                          className="btn btn-circle btn-xs bg-gray-600 hover:bg-gray-500 text-white transition-colors duration-150 disabled:opacity-40"
                          onClick={() => pastePane(ind)}
                          onMouseDown={stopPropagation}
                          onTouchStart={stopPropagation}
                          disabled={!copiedPane}
                        >
                          <FontAwesomeIcon icon={faPaste} size="xs" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Nested Widgets (also locked if no edit) */}
              <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: '1 1 auto', scrollbarWidth: 'thin' }}>
                <ResponsiveGridLayout
                  className="layout nested-grid"
                  layouts={getNestedWidgetLayouts(pane.widgetsArray || [], ind)}
                  breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                  cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
                  rowHeight={30}
                  onLayoutChange={(layout) => onWidgetLayoutChange(layout, ind)}
                  isDraggable={canEdit}
                  isResizable={canEdit}
                  draggableCancel=".no-drag-zone"
                >
                  {pane.widgetsArray?.map((widget, idx) => renderWidget(widget, ind, idx))}
                </ResponsiveGridLayout>
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>

        {modal && (
          <DropDownModal
            toggleModal={toggleModal}
            widgetSettupHandler={widgetSettupHandler}
          />
        )}
        {editWidgetModal && (
          <DropDownModal
            editWidgeToggleModal={editWidgeToggleModal}
            widgetSettupHandler={widgetSettupHandler}
            editWidgetModal={editWidgetModal}
            paneArray={props.paneArray}
            setPaneArray={props.setPaneArray}
            widget={getCurrentWidget()}
            currentTarget={currentTarget}
          />
        )}
        {changeSourceModal && (
          <WidgetSettingModal
            setChangeSourceModal={setChangeSourceModal}
            paneArray={props.paneArray}
            setPaneArray={props.setPaneArray}
            widgetPath={currentTarget.path}
            widget={getCurrentWidget()}
          />
        )}
      </div>

    </>
  );
});

MuuriWidget.propTypes = {
  onLayoutChange: PropTypes.func.isRequired,
  paneArray: PropTypes.array.isRequired,
  setPaneArray: PropTypes.func.isRequired,
  newDashboard: PropTypes.string,
};

MuuriWidget.defaultProps = {
  className: "layout",
  rowHeight: 30,
  onLayoutChange: function () { },
  cols: { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 },
};

export default MuuriWidget;